// A Tally export or a sheet of masters LAMP read as data to bring in (#503): the new customers, suppliers and Items
// confirming brings in and those already in LAMP it only links to, each opening balance and opening stock line it posts
// with its amount or quantity (#546), and each row it couldn't read or won't bring in, where it is and why. LAMP saves
// nothing until the person confirms; this only shows the preview.
import type { ReactNode } from "react";
import { rupees, type DataImportPreview as Preview } from "../api.js";
import { formatNumber } from "../i18n/format.js";
import { useLanguage, useWords } from "../i18n/words.js";

/** Lines beyond this many are collapsed, so a long file's card stays short until the person opens it. */
const SHOWN_LINES = 10;

/** A table of a preview's lines, collapsed when it is long. */
function Lines({
  caption,
  headers,
  rows,
}: {
  readonly caption: string;
  readonly headers: readonly string[];
  readonly rows: readonly (readonly ReactNode[])[];
}) {
  return (
    <details open={rows.length <= SHOWN_LINES}>
      <summary>{`${caption} (${rows.length})`}</summary>
      <table className="lines" aria-label={caption}>
        <thead>
          <tr>
            {headers.map((header) => (
              <th key={header} scope="col">
                {header}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {rows.map((row, i) => (
            <tr key={i}>
              {row.map((cell, j) => (
                <td key={j}>{cell}</td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </details>
  );
}

export function DataImportPreview({ preview }: { readonly preview: Preview }) {
  const w = useWords();
  const { language } = useLanguage();
  const linked = preview.linkedCounterparties + preview.linkedItems;
  const rows: [string, string][] = [
    ...(preview.customers > 0 ? [[w.fileImportCustomers, String(preview.customers)] as [string, string]] : []),
    ...(preview.suppliers > 0 ? [[w.fileImportSuppliers, String(preview.suppliers)] as [string, string]] : []),
    ...(preview.jobWorkers > 0 ? [[w.fileImportJobWorkers, String(preview.jobWorkers)] as [string, string]] : []),
    ...(preview.items > 0 ? [[w.fileImportItems, String(preview.items)] as [string, string]] : []),
    ...(linked > 0 ? [[w.fileImportLinked, String(linked)] as [string, string]] : []),
    ...(preview.openingBalances > 0
      ? [
          [w.fileImportOwedToYou, rupees(preview.receivablePaise)] as [string, string],
          [w.fileImportYouOwe, rupees(preview.payablePaise)] as [string, string],
        ]
      : []),
  ];
  const created = preview.newCounterparties + preview.newItems;
  return (
    <section className="file-draft" aria-label={w.fileRead}>
      <p className="muted">
        {w.fileImportPreview(preview.number, preview.asOf)}
        {created > 0 ? ` · ${w.fileImportNew(created)}` : ""}
      </p>
      {rows.length > 0 ? (
        <dl>
          {rows.map(([label, value]) => (
            <div key={label}>
              <dt>{label}</dt>
              <dd>{value}</dd>
            </div>
          ))}
        </dl>
      ) : null}
      {preview.balanceLines.length > 0 ? (
        <Lines
          caption={w.fileImportBalances}
          headers={[w.fileImportName, w.fileImportOwedToYou, w.fileImportYouOwe]}
          rows={preview.balanceLines.map((line) => [
            line.name,
            line.receivablePaise !== 0 ? rupees(line.receivablePaise) : "",
            line.payablePaise !== 0 ? rupees(line.payablePaise) : "",
          ])}
        />
      ) : null}
      {preview.stockLines.length > 0 ? (
        <Lines
          caption={w.fileImportStock}
          headers={[w.fileImportItem, w.fileImportQuantity, w.fileImportUnit]}
          rows={preview.stockLines.map((line) => [
            line.name,
            formatNumber(line.quantity, language, { maximumFractionDigits: 3 }),
            line.unit,
          ])}
        />
      ) : null}
      {preview.panLines && preview.panLines.length > 0 ? (
        <Lines
          caption={w.fileImportPans}
          headers={[w.fileImportName, w.taxPan]}
          rows={preview.panLines.map((line) => [line.name, line.pan])}
        />
      ) : null}
      {preview.already > 0 ? <p className="muted">{w.fileImportAlready(preview.already)}</p> : null}
      {preview.notBrought.length > 0 ? (
        <table className="lines" aria-label={w.fileImportNotBrought}>
          <caption>{w.fileImportNotBrought}</caption>
          <thead>
            <tr>
              <th scope="col">{w.fileImportWhat}</th>
              <th scope="col">{w.fileImportName}</th>
              <th scope="col">{w.fileImportWhy}</th>
            </tr>
          </thead>
          <tbody>
            {preview.notBrought.map((row, i) => (
              <tr key={i}>
                <td>{row.what}</td>
                <td>{row.name}</td>
                <td>{row.reason}</td>
              </tr>
            ))}
          </tbody>
        </table>
      ) : null}
    </section>
  );
}
